import { fontAttrs } from "@/components/tools/printables/page-kit";
import { INK, MUTED } from "@/components/tools/printables/sheet-header";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import type {
  KeyPage,
  PracticeColumns,
  PracticePage,
  SpellingEntry,
  TestPage,
} from "@/lib/tool-kit/printables/spelling";
import { PRACTICE_LABEL_ROW } from "@/lib/tool-kit/printables/spelling";
import {
  type FontSpec,
  fitSize,
  type TextMeasure,
  truncateToFit,
  wrapText,
} from "@/lib/tool-kit/printables/text";

const SANS: FontSpec = { kind: "sans" };
const BOLD: FontSpec = { kind: "sans", bold: true };
const RULE = "#9a9a9a";
const BAND = "#ececec";

const r = (value: number) => Math.round(value * 100) / 100;

/** The numbered test: a number and a line for each word, and any dictation lines. */
export function TestSheet({
  page,
  rowHeight,
  geometry,
  sentenceLabel,
}: {
  page: TestPage;
  rowHeight: number;
  geometry: PageGeometry;
  /** "Sentence" — prefixes the dictation lines' numbers. */
  sentenceLabel: string;
}) {
  const right = geometry.left + geometry.contentWidth;
  return (
    <g>
      {page.cells.map((cell) => {
        const base = r(cell.y + rowHeight - 2);
        return (
          <g key={cell.number}>
            <text
              fill={MUTED}
              fontSize={4.2}
              x={r(cell.x)}
              y={base}
              {...fontAttrs(SANS)}
            >
              {cell.number}.
            </text>
            <line
              stroke={INK}
              strokeWidth={0.25}
              x1={r(cell.x + 9)}
              x2={r(cell.x + cell.width)}
              y1={base}
              y2={base}
            />
          </g>
        );
      })}
      {page.sentences.map((sentence) => {
        const base = r(sentence.y + rowHeight * 1.4 - 2);
        return (
          <g key={sentence.number}>
            <text
              fill={MUTED}
              fontSize={4.2}
              x={geometry.left}
              y={base}
              {...fontAttrs(SANS)}
            >
              {sentenceLabel}
            </text>
            <line
              stroke={INK}
              strokeWidth={0.25}
              x1={r(geometry.left + 22)}
              x2={r(right)}
              y1={base}
              y2={base}
            />
          </g>
        );
      })}
    </g>
  );
}

/** The look–say–cover–write–check grid. */
export function PracticeSheet({
  page,
  columns,
  entries,
  rowHeight,
  geometry,
  labels,
  trace,
  measure,
}: {
  page: PracticePage;
  columns: PracticeColumns;
  entries: readonly SpellingEntry[];
  rowHeight: number;
  geometry: PageGeometry;
  labels: { look: string; write: string; check: string };
  trace: boolean;
  measure: TextMeasure;
}) {
  const bottomY = page.rows.length
    ? (page.rows.at(-1)?.y ?? 0) + rowHeight
    : page.headerY + PRACTICE_LABEL_ROW;
  const writeLeft = columns.write[0]?.x ?? columns.word.x;
  const writeRight = columns.check.x;
  const dividers = [
    columns.word.x,
    ...columns.write.map((column) => column.x),
    columns.check.x,
    columns.right,
  ];
  return (
    <g>
      <rect
        fill={BAND}
        height={PRACTICE_LABEL_ROW}
        width={r(geometry.contentWidth)}
        x={r(geometry.left)}
        y={r(page.headerY)}
      />
      <g fill={INK} fontSize={3.8} {...fontAttrs(BOLD)}>
        <text x={r(columns.word.x + 2)} y={r(page.headerY + 6)}>
          {labels.look}
        </text>
        <text x={r(writeLeft + 2)} y={r(page.headerY + 6)}>
          {labels.write}
        </text>
        <text x={r(columns.check.x + 2)} y={r(page.headerY + 6)}>
          {labels.check}
        </text>
      </g>
      {page.rows.map((row) => {
        const entry = entries[row.entryIndex];
        const word = entry?.word ?? "";
        const size = word
          ? fitSize(measure, word, SANS, columns.word.width - 14, 7.5, 3)
          : 0;
        const base = r(row.y + rowHeight * 0.68);
        const first = columns.write[0];
        return (
          <g key={row.number}>
            <line
              stroke={RULE}
              strokeWidth={0.25}
              x1={r(geometry.left)}
              x2={r(columns.right)}
              y1={r(row.y + rowHeight)}
              y2={r(row.y + rowHeight)}
            />
            <text
              fill={MUTED}
              fontSize={3.4}
              x={r(columns.word.x + 1.5)}
              y={base}
              {...fontAttrs(SANS)}
            >
              {row.number}
            </text>
            {word ? (
              <text
                fill={INK}
                fontSize={r(size)}
                x={r(columns.word.x + 8)}
                y={base}
                {...fontAttrs(SANS)}
              >
                {word}
              </text>
            ) : null}
            {trace && word && first ? (
              <text
                fill="none"
                fontSize={r(
                  fitSize(measure, word, SANS, first.width - 6, 7.5, 3)
                )}
                stroke="#777777"
                strokeDasharray="0.4 0.4"
                strokeWidth={0.25}
                x={r(first.x + 3)}
                y={base}
                {...fontAttrs(SANS)}
              >
                {word}
              </text>
            ) : null}
            <rect
              fill="none"
              height={5}
              stroke={INK}
              strokeWidth={0.3}
              width={5}
              x={r(columns.check.x + (columns.check.width - 5) / 2)}
              y={r(row.y + (rowHeight - 5) / 2)}
            />
          </g>
        );
      })}
      {dividers.map((x, index) => (
        <line
          key={x}
          stroke={RULE}
          strokeWidth={0.25}
          x1={r(x)}
          x2={r(x)}
          // The label over the write-it boxes spans them, so inner dividers start below it.
          y1={r(
            index > 1 && index < dividers.length - 2
              ? page.headerY + PRACTICE_LABEL_ROW
              : page.headerY
          )}
          y2={r(bottomY)}
        />
      ))}
      <line
        stroke={RULE}
        strokeWidth={0.25}
        x1={r(geometry.left)}
        x2={r(columns.right)}
        y1={r(page.headerY)}
        y2={r(page.headerY)}
      />
      <line
        stroke={RULE}
        strokeWidth={0.25}
        x1={r(writeLeft)}
        x2={r(writeRight)}
        y1={r(bottomY)}
        y2={r(bottomY)}
      />
    </g>
  );
}

/** The teacher's copy: the words in test order, each with its dictation sentence. */
export function KeySheet({
  page,
  entries,
  measure,
}: {
  page: KeyPage;
  entries: readonly SpellingEntry[];
  measure: TextMeasure;
}) {
  return (
    <g>
      {page.rows.map((row) => {
        const entry = entries[row.entryIndex];
        const sentence = entry?.sentence ?? "";
        const lines = sentence
          ? wrapText(measure, sentence, SANS, 3.6, row.width - 9).slice(0, 2)
          : [];
        const wrapped = sentence
          ? wrapText(measure, sentence, SANS, 3.6, row.width - 9)
          : [];
        const shown =
          wrapped.length > 2 && lines[1]
            ? [
                lines[0] ?? "",
                truncateToFit(
                  measure,
                  `${lines[1]} ${wrapped.slice(2).join(" ")}`,
                  SANS,
                  3.6,
                  row.width - 9
                ),
              ]
            : lines;
        return (
          <g key={row.number}>
            <text
              fill={MUTED}
              fontSize={4}
              x={r(row.x)}
              y={r(row.y + 5.5)}
              {...fontAttrs(SANS)}
            >
              {row.number}.
            </text>
            <text
              fill={INK}
              fontSize={5.2}
              x={r(row.x + 9)}
              y={r(row.y + 5.5)}
              {...fontAttrs(BOLD)}
            >
              {entry?.word}
            </text>
            {shown.map((line, index) => (
              <text
                fill={MUTED}
                fontSize={3.6}
                // biome-ignore lint/suspicious/noArrayIndexKey: lines are positional
                key={index}
                x={r(row.x + 9)}
                y={r(row.y + 10.5 + index * 4.2)}
                {...fontAttrs(SANS)}
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}
    </g>
  );
}
